import { useEffect, type CSSProperties } from "react";
import {
  Pause,
  Play,
  Repeat,
  Repeat1,
  Shuffle,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
} from "lucide-react";
import { Button } from "@web/components/ui/button.tsx";
import { EntityAvatar } from "@web/components/EntityAvatar.tsx";
import { cn } from "@web/lib/utils.ts";
import { usePlayerStore } from "./playerStore.ts";
import { formatPlayerTime } from "./types.ts";
import "./player.css";

/** Progress fill for .coykoi-player-range (see player.css). */
type PlayerRangeStyle = CSSProperties & {
  "--coykoi-range-progress": string;
};

function playerRangeStyle(pct: number): PlayerRangeStyle {
  return { "--coykoi-range-progress": `${pct}%` };
}

/**
 * Spotify-like player bar. SDK init is lazy on first play, but we warm it on
 * mount so device_id is ready when the user hits play on a row.
 */
export function PlayerBar() {
  const init = usePlayerStore((s) => s.init);
  const ready = usePlayerStore((s) => s.ready);
  const playing = usePlayerStore((s) => s.playing);
  const shuffle = usePlayerStore((s) => s.shuffle);
  const repeat = usePlayerStore((s) => s.repeat);
  const volume = usePlayerStore((s) => s.volume);
  const positionMs = usePlayerStore((s) => s.positionMs);
  const durationMs = usePlayerStore((s) => s.durationMs);
  const nowPlaying = usePlayerStore((s) => s.nowPlaying);
  const status = usePlayerStore((s) => s.status);
  const queue = usePlayerStore((s) => s.queue);

  const togglePlay = usePlayerStore((s) => s.togglePlay);
  const next = usePlayerStore((s) => s.next);
  const previous = usePlayerStore((s) => s.previous);
  const toggleShuffle = usePlayerStore((s) => s.toggleShuffle);
  const cycleRepeat = usePlayerStore((s) => s.cycleRepeat);
  const setVolume = usePlayerStore((s) => s.setVolume);
  const beginSeek = usePlayerStore((s) => s.beginSeek);
  const previewSeek = usePlayerStore((s) => s.previewSeek);
  const commitSeek = usePlayerStore((s) => s.commitSeek);

  useEffect(() => {
    void init().catch(() => {
      /* status already set in the store */
    });
  }, [init]);

  const hasQueue = queue.length > 0;
  const seekMax = 1000;
  const seekValue = durationMs > 0 ? Math.round((positionMs / durationMs) * seekMax) : 0;
  const seekPct = durationMs > 0 ? (seekValue / seekMax) * 100 : 0;
  const volumePct = Math.round(volume * 100);
  const muted = volume <= 0.001;

  const repeatLabel =
    repeat === "off" ? "Repeat off" : repeat === "context" ? "Repeat playlist" : "Repeat song";

  return (
    <footer
      className="flex h-player shrink-0 items-center gap-4 rounded-lg bg-panel px-4"
      aria-label="Spotify player"
      data-ready={ready ? "true" : "false"}
    >
      <div className="flex w-sidebar min-w-0 items-center gap-3">
        <EntityAvatar src={nowPlaying?.coverUrl} alt="" size="lg" className="size-14" />
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-text">
            {nowPlaying?.name ?? "Choose a song"}
          </div>
          <div className="truncate text-xs text-muted">
            {nowPlaying?.artists || "Spotify playback"}
          </div>
          {status.message ? (
            <div
              className={cn(
                "truncate text-xs",
                status.isError ? "text-destructive" : "text-faint",
              )}
            >
              {status.message}
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex flex-1 flex-col items-center gap-1">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={shuffle ? "Shuffle on" : "Shuffle off"}
            aria-pressed={shuffle}
            className={cn(shuffle && "text-accent hover:text-accent")}
            onClick={toggleShuffle}
            disabled={!hasQueue}
          >
            <Shuffle className="size-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Previous"
            onClick={() => void previous()}
            disabled={!hasQueue}
          >
            <SkipBack className="size-4 fill-current" />
          </Button>
          <Button
            variant="secondary"
            size="icon"
            className="size-8 bg-text text-accent-contrast hover:scale-105 hover:bg-text"
            aria-label={playing ? "Pause" : "Play"}
            onClick={() => void togglePlay()}
            disabled={!hasQueue && !nowPlaying}
          >
            {playing ? (
              <Pause className="size-4 fill-current" />
            ) : (
              <Play className="size-4 fill-current" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Next"
            onClick={() => void next()}
            disabled={!hasQueue}
          >
            <SkipForward className="size-4 fill-current" />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={repeatLabel}
            title={repeatLabel}
            aria-pressed={repeat !== "off"}
            className={cn(repeat !== "off" && "text-accent hover:text-accent")}
            onClick={cycleRepeat}
          >
            {repeat === "track" ? (
              <Repeat1 className="size-3.5" />
            ) : (
              <Repeat className="size-3.5" />
            )}
          </Button>
        </div>
        <div className="flex w-full max-w-md items-center gap-2 text-xs text-faint">
          <span className="w-8 text-right tabular-nums">{formatPlayerTime(positionMs)}</span>
          <input
            type="range"
            className="coykoi-player-range"
            min={0}
            max={seekMax}
            step={1}
            value={seekValue}
            aria-label="Seek"
            disabled={!hasQueue && !nowPlaying}
            style={playerRangeStyle(seekPct)}
            onPointerDown={beginSeek}
            onChange={(e) => {
              const ratio = Number(e.target.value) / seekMax;
              previewSeek(ratio);
            }}
            onMouseUp={(e) => {
              const ratio = Number(e.currentTarget.value) / seekMax;
              void commitSeek(ratio);
            }}
            onTouchEnd={(e) => {
              const ratio = Number(e.currentTarget.value) / seekMax;
              void commitSeek(ratio);
            }}
            onKeyUp={(e) => {
              if (
                e.key === "ArrowLeft" ||
                e.key === "ArrowRight" ||
                e.key === "Home" ||
                e.key === "End"
              ) {
                const ratio = Number(e.currentTarget.value) / seekMax;
                void commitSeek(ratio);
              }
            }}
          />
          <span className="w-8 tabular-nums">{formatPlayerTime(durationMs)}</span>
        </div>
      </div>

      <div className="flex w-40 items-center justify-end gap-2 text-muted">
        <button
          type="button"
          className="rounded-md p-1 hover:text-text"
          aria-label={muted ? "Unmute" : "Mute"}
          onClick={() => void setVolume(muted ? 0.8 : 0)}
        >
          {muted ? <VolumeX className="size-4" /> : <Volume2 className="size-4" />}
        </button>
        <input
          type="range"
          className="coykoi-player-range w-24"
          min={0}
          max={100}
          step={1}
          value={volumePct}
          aria-label="Volume"
          style={playerRangeStyle(volumePct)}
          onChange={(e) => void setVolume(Number(e.target.value) / 100)}
        />
      </div>
    </footer>
  );
}
